// Turns a stream of wheel deltas into discrete page flips: one flick is one
// flip. A mouse wheel sends a few large ticks, a trackpad sends a long tail
// of shrinking inertia events after the finger lifts, and both have to land
// exactly one page further. So once a flip fires, the pager ignores input
// until the stream goes quiet or a fresh swipe starts (a delta that jumps
// well above the decaying tail). A mouse wheel spun steadily never goes
// quiet, so a big tick also unlocks once the last flip has mostly landed.
// A hard flick ramps up before it decays, so both are judged against the
// biggest delta since the flip: a fresh swipe must follow a tail that has
// fallen well below it, and a repeat must still be near it.

const THRESHOLD = 36
const QUIET_MS = 170
const FRESH_RATIO = 1.8
const FRESH_MIN = 18
const REPEAT_MS = 360
const REPEAT_MIN = 40
const DECAYED = 0.5
const STEADY = 0.8

export class WheelPager {
  private sum = 0
  private locked = false
  private lastTime = 0
  private lastSize = 0
  private flipTime = 0
  private peak = 0

  // Returns -1, 0 or 1: the flip this event triggers.
  push(delta: number, now = performance.now()): number {
    const size = Math.abs(delta)
    const quiet = now - this.lastTime > QUIET_MS
    const fresh =
      size >= FRESH_MIN &&
      size > this.lastSize * FRESH_RATIO &&
      this.lastSize < this.peak * DECAYED &&
      !quiet
    const repeat =
      size >= REPEAT_MIN &&
      size >= this.peak * STEADY &&
      now - this.flipTime > REPEAT_MS
    if (quiet || (this.locked && (fresh || repeat))) {
      this.locked = false
      this.sum = 0
      this.peak = 0
    }
    this.peak = Math.max(this.peak, size)
    this.lastTime = now
    this.lastSize = size
    if (this.locked) return 0
    if (this.sum !== 0 && Math.sign(delta) !== Math.sign(this.sum)) this.sum = 0
    this.sum += delta
    if (Math.abs(this.sum) < THRESHOLD) return 0
    this.locked = true
    this.flipTime = now
    const direction = Math.sign(this.sum)
    this.sum = 0
    return direction
  }

  reset() {
    this.sum = 0
    this.locked = false
    this.peak = 0
  }
}
